import { CommonModule, Location } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Component, OnInit, ChangeDetectorRef, Renderer2, ElementRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputSwitchModule } from 'primeng/inputswitch';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { UserService } from 'src/app/core/services/user.service';
import {
  User,
  UserData,
  UserFilter,
  UserResponse,
  UserFilterParams,
} from 'src/app/shared/model/user.model';
import { domainUrl } from 'src/environments/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { CountryResponse, RegionResponse } from 'src/app/shared/model/common.model';
import { ToastModule } from 'primeng/toast';
import {
  TABLE_PARAM,
  USER_TYPES,
  STATUSES,
  USER_STATUS,
} from 'src/app/shared/constants/common.constants';
import httpErrorCodes from 'src/app/core/common/http_error_codes';
import { PaginationService } from 'src/app/shared/providers/pagination/pagination.service';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    HttpClientModule,
    TableModule,
    ButtonModule,
    DropdownModule,
    CalendarModule,
    InputTextModule,
    InputNumberModule,
    FormsModule,
    ToastModule,
    TagModule,
    InputSwitchModule,
  ],
  providers: [UserService, TranslateService],
  animations: [],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
})
export class UsersComponent implements OnInit {
  tableParams = { ...TABLE_PARAM };

  userTypeConstants = USER_TYPES;

  userStatus = USER_STATUS;

  baseApiUrl = domainUrl.url;

  noDataRow = [{}];

  totalRecords = 0;

  filters: UserFilter = {
    nickName: '',
    firstName: '',
    phoneNumber: '',
    regionId: '',
    countryId: '',
    dateRange: null,
    paymentProfile: '',
    status: '',
  };

  activeFilter = '';

  userData: UserResponse['data'] | null = null;

  userItems: UserData['items'] | [] = [];

  userPageNumber: UserData['pageNumber'] = TABLE_PARAM.PAGE_NUMBER;

  userPageSize: UserData['pageSize'] = TABLE_PARAM.PAGE_SIZE;

  regionIds!: RegionResponse['data'];

  paymentProfiles = [
    { label: 'Completed', value: 'Completed' },
    { label: 'Pending', value: 'Pending' },
  ];

  statusOptions: { label: string; value: string }[] = [];

  countryIds!: CountryResponse['data'];

  first = 0;

  rows = TABLE_PARAM.PAGE_SIZE;

  sortField = 'CreatedOn';

  sortOrder = TABLE_PARAM.SORT_DEC_ORDER;

  filePath: UserResponse['filePath'];

  constructor(
    private userService: UserService,
    private router: Router,
    private location: Location,
    private translateService: TranslateService,
    private toastService: ToasterService,
    private cdRef: ChangeDetectorRef,
    private commonService: CommonService,
    private el: ElementRef,
    private renderer: Renderer2,
    private paginationService: PaginationService,
  ) {}

  ngOnInit() {
    this.statusOptions = [
      { label: this.translateService.instant('USER_PAGE.FILTER_LABELS.ALL'), value: '' },
      { label: this.translateService.instant('USER_PAGE.FILTER_LABELS.BLOCKED'), value: 'true' },
      { label: this.translateService.instant('USER_PAGE.FILTER_LABELS.UNBLOCKED'), value: 'false' },
    ];
    this.rows = this.paginationService.getPageSize('user');
    this.tableParams.ROW_PER_PAGE = this.paginationService.getAllowedSizes();
    this.userPageSize = this.rows;
    this.userPageNumber = 1;

    this.loadData(
      this.userPageNumber,
      this.userPageSize,
      this.sortField,
      this.sortOrder,
      false,
      this.filters.paymentProfile,
    );
    this.getCountries(this.activeFilter);
  }

  formatFullName(user: { firstName: string; lastName: string }): string {
    const fullName = [user.firstName, user.lastName].filter((name) => !!name).join(' ');
    return fullName;
  }

  getConvertedDate(utcDate: string) {
    return CommonService.getFormattedDate(utcDate);
  }

  pageChange(event: { first: number; rows: number }) {
    this.first = event.first;
    this.rows = event.rows;
    this.paginationService.setPageSize('user', this.rows);
    this.userPageNumber = this.first / this.rows;
    this.userPageSize = this.rows;
    this.loadData(
      this.userPageNumber + 1,
      this.rows,
      this.sortField,
      this.sortOrder,
      false,
      this.filters.paymentProfile,
    );
  }

  sortChange(event: { field: string; order: string }) {
    if (event.field === 'PaymentProfile') {
      this.sortField = 'accountKycStatus';
    } else {
      this.sortField = event.field;
    }

    this.sortOrder =
      String(event.order) === '1' ? TABLE_PARAM.SORT_ASC_ORDER : TABLE_PARAM.SORT_DEC_ORDER;
    this.loadData(
      this.userPageNumber,
      this.userPageSize,
      this.sortField,
      this.sortOrder,
      false,
      this.filters.paymentProfile,
    );
  }

  goToUserDetailsPage(user: User) {
    const tree = this.router.createUrlTree([`/user-details/${user.id}/${user.userType}`]);
    const url = this.router.serializeUrl(tree);
    const fullUrl = window.location.origin + this.location.prepareExternalUrl(url);
    window.open(fullUrl, '_blank');
  }

  getRegions(countryIds: number) {
    this.commonService.getRegions([countryIds]).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.regionIds = response.data;
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'USER_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_FETCH_REGION_DATA',
            ),
          );
        }
      },
    });
  }

  getCountries(userType: string) {
    this.commonService.getCountry(userType).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.countryIds = response.data;
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'USER_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_FETCH_COUNTRY_DATA',
            ),
          );
        }
      },
    });
  }

  // Apply Filters
  applyFilters() {
    this.first = 0;
    this.userPageNumber = this.userPageNumber === 0 ? 1 : this.userPageNumber;
    const paymentProfilesStatus = this.filters.paymentProfile || '';
    this.loadData(
      this.userPageNumber,
      this.userPageSize,
      this.sortField,
      this.sortOrder,
      false,
      paymentProfilesStatus,
    );
  }

  loadData(
    userPageNumber: number,
    userPageSize: number,
    sortField: string,
    sortOrder: string,
    isExport: boolean,
    paymentProfile: string,
  ) {
    const formattedDates =
      this.filters?.dateRange?.map((date) => CommonService.formatDate(date?.toString())) || [];

    const isValidDate = (date: string) => date && !date.includes('NaN');

    const startDate = isValidDate(formattedDates[0]) ? formattedDates[0] : '';
    const endDate = isValidDate(formattedDates[1]) ? formattedDates[1] : '';

    const regionIdValue = (
      this.filters.regionId as unknown as { id: number; regionName: string; countryId: number }
    ).id;
    const countryIdValue = (
      this.filters.countryId as unknown as { id: number; countryName: string; phoneCode: number }
    ).id;

    this.getUserData(
      userPageNumber,
      userPageSize,
      this.filters.nickName,
      this.filters.firstName,
      this.filters.phoneNumber,
      regionIdValue || '',
      countryIdValue || '',
      startDate,
      endDate,
      this.activeFilter,
      sortField,
      sortOrder,
      this.filters.status || '',
      isExport,
      paymentProfile || '',
    );
  }

  // Reset Pagination on Filter
  onFilterChange(type: string | null = null) {
    if (type !== null && type !== undefined) {
      this.userPageNumber = 1;
      this.activeFilter = type;
      this.getCountries(this.activeFilter);
      const countryValue = (
        this.filters.countryId as unknown as { id: number; countryName: string; phoneCode: number }
      ).id;

      if (countryValue !== undefined) {
        this.getRegions(countryValue);
      }

      // Reset payment profile filter when filter type changes
      if (this.activeFilter !== this.userTypeConstants.TALKING_BUDDY) {
        this.filters.paymentProfile = '';
      }

      this.applyFilters();
      return;
    }

    if (this.activeFilter === this.userTypeConstants.TALKING_BUDDY) {
      this.userPageNumber = 1;
      this.applyFilters();
      return;
    }

    this.applyFilters();
  }

  updateBlockStatus(user: User) {
    const newStatus = !user.isBlocked;

    this.userService.updateBlockStatus(user.id, newStatus).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          // eslint-disable-next-line no-param-reassign
          user.isBlocked = newStatus;
          this.toastService.showSuccessToast(
            this.translateService.instant('USER_PAGE.TOAST_MESSAGE.BLOCK_STATUS_UPDATED'),
          );
        } else {
          this.toastService.showErrorToast(
            this.translateService.instant('USER_PAGE.TOAST_MESSAGE.BLOCK_STATUS_UPDATE_FAILED'),
          );
          this.cdRef.detectChanges();
        }
      },
      error: () => {
        this.toastService.showErrorToast(
          this.translateService.instant('USER_PAGE.TOAST_MESSAGE.BLOCK_STATUS_UPDATE_FAILED'),
        );
        this.cdRef.detectChanges();
      },
    });
  }

  getUserData(
    pageNumber: number,
    pageSize: number,
    nickName: string,
    name: string,
    phoneNumber: string,
    regionId: number | string,
    countryId: number | string,
    createdFrom: string,
    createdTo: string,
    userType: string,
    sortField: string,
    sortOrder: string,
    blockedUserStatus: string,
    isExport: boolean,
    paymentProfileStatus?: string,
  ): void {
    const params: UserFilterParams = {
      pageNumber,
      pageSize,
      nickName,
      name,
      phoneNumber,
      regionId,
      countryId,
      createdFrom,
      createdTo,
      userType,
      sortField,
      sortOrder,
      blockedUserStatus,
      isExport,
      paymentProfileStatus,
    };
    this.userService.getUserFilteredData(params).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          if (isExport === true) {
            this.filePath = this.baseApiUrl + response.filePath;

            if (this.filePath) {
              const fileName = this.filePath.split('/').pop() || '';
              CommonService.downloadFile(this.filePath, fileName);
            } else {
              this.toastService.showErrorToast(
                this.translateService.instant(
                  'USER_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_EXPORT_USER_DATA',
                ),
              );
            }
          } else {
            this.userData = response.data;
            this.userItems = this.userData.items;
            this.totalRecords = this.userData.totalCount;
          }

          // Trigger change detection to update the table
          this.cdRef.detectChanges();
        }
      },
      error: (err) => {
        if (isExport === true) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'USER_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_EXPORT_USER_DATA',
            ),
          );
        } else if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant('DASHBOARD_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_USER_DATA'),
          );
        }
      },
    });
  }

  exportExcel() {
    this.loadData(
      this.userPageNumber,
      this.userPageSize,
      this.sortField,
      this.sortOrder,
      true,
      this.filters.paymentProfile,
    );
    this.toastService.showSuccessToast(
      this.translateService.instant('USER_DETAILS_PAGE.TOAST_MESSAGE.SUCCESS_TO_EXPORT_USER_DATA'),
    );
  }

  onResetFilters() {
    this.filters = {
      nickName: '',
      firstName: '',
      phoneNumber: '',
      regionId: '',
      countryId: '',
      dateRange: null,
      paymentProfile: '',
    };
    this.activeFilter = '';
    this.userPageNumber = 1;
    this.applyFilters();
  }

  onCalendarOpen() {
    const calendarElement = this.el.nativeElement.querySelector(
      '.p-calendar .p-datepicker:not(.p-datepicker-inline)',
    );

    if (calendarElement) {
      const transformOrigin = window
        .getComputedStyle(calendarElement)
        .getPropertyValue('transform-origin');
      const isWithin370Range = /37[0-9]/.test(transformOrigin);

      if (isWithin370Range) {
        this.renderer.addClass(calendarElement, 'custom-transform-origin');
      } else {
        this.renderer.removeClass(calendarElement, 'custom-transform-origin');
      }
    }
  }
}
